// src/pages/Register.jsx
import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Spinner } from 'react-bootstrap';
import api from '../services/api';
import AuthShell, { IconEye, IconArrow, IconBack } from '../components/auth/AuthShell';


const ROLE_CFG = {
    admin:      { label: 'Administrador', color: '#7c3aed', bg: '#f5f3ff' },
    agent:      { label: 'Agente',        color: '#0284c7', bg: '#e0f2fe' },
    leader:     { label: 'Líder de área', color: '#7c3aed', bg: '#f5f3ff' },
    authorizer: { label: 'Autorizador',   color: '#b45309', bg: '#fef3c7' },
    user:       { label: 'Usuario',       color: '#059669', bg: '#ecfdf5' },
};


export default function Register() {
    const [searchParams]  = useSearchParams();
    const invitationToken = searchParams.get('token');
    const navigate        = useNavigate();

    const [invitationInfo,  setInvitationInfo]  = useState(null);
    const [invitationError, setInvitationError] = useState('');
    const [loadingInv,      setLoadingInv]      = useState(true);

    const [name,     setName]     = useState('');
    const [password, setPassword] = useState('');
    const [confirm,  setConfirm]  = useState('');
    const [showPass, setShowPass] = useState(false);
    const [loading,  setLoading]  = useState(false);

    useEffect(() => {
        if (!invitationToken) {
            setInvitationError('No se encontró un token de invitación. Por favor usa el link que recibiste por correo.');
            setLoadingInv(false);
            return;
        }
        api.get(`/auth/invitation?token=${invitationToken}`)
            .then(({ data }) => setInvitationInfo(data))
            .catch(() => setInvitationError('La invitación es inválida o ya expiró. Contacta al administrador.'))
            .finally(() => setLoadingInv(false));
    }, [invitationToken]);

    const handleSubmit = async e => {
        e.preventDefault();
        if (!name.trim())         { toast.warn('El nombre es obligatorio'); return; }
        if (password.length < 6)  { toast.warn('La contraseña debe tener al menos 6 caracteres'); return; }
        if (password !== confirm) { toast.warn('Las contraseñas no coinciden'); return; }

        setLoading(true);
        try {
            await api.post('/auth/register', { name: name.trim(), password, invitationToken });
            toast.success('Registro exitoso. Ya puedes iniciar sesión.', { autoClose: 4000 });
            navigate('/login');
        } catch (err) {
            toast.error(err.response?.data?.error || 'Error al registrarse');
        } finally {
            setLoading(false);
        }
    };

    // ── Cargando ──────────────────────────────────────────────────────────────
    if (loadingInv) {
        return (
            <AuthShell>
                <div className="auth-loading">
                    <Spinner animation="border" style={{ color: 'var(--brand-700)' }} />
                    <p>Verificando invitación…</p>
                </div>
            </AuthShell>
        );
    }

    // ── Error de invitación ───────────────────────────────────────────────────
    if (invitationError) {
        return (
            <AuthShell>
                <h1 className="auth-title">Invitación no válida</h1>
                <p className="auth-subtitle">{invitationError}</p>
                <Link to="/login" className="auth-submit" style={{ background: 'transparent', color: 'var(--brand-700)', border: '1px solid var(--c-border)', boxShadow: 'none', textDecoration: 'none' }}>
                    <IconBack /> Volver al inicio de sesión
                </Link>
            </AuthShell>
        );
    }

    // ── Formulario ────────────────────────────────────────────────────────────
    const role    = invitationInfo?.role || 'user';
    const roleCfg = ROLE_CFG[role] || ROLE_CFG.user;

    return (
        <AuthShell>
            <h1 className="auth-title">Completa tu registro</h1>
            <p className="auth-subtitle">
                Fuiste invitado como{' '}
                <span className="auth-role-chip" style={{ background: roleCfg.bg, color: roleCfg.color }}>
                    {roleCfg.label}
                </span>
            </p>

            <form onSubmit={handleSubmit}>
                <div className="auth-field">
                    <label className="auth-label" htmlFor="rg-email">Correo electrónico</label>
                    <input
                        id="rg-email"
                        type="email"
                        className="auth-input"
                        value={invitationInfo.email}
                        disabled
                    />
                </div>

                <div className="auth-field">
                    <label className="auth-label" htmlFor="rg-name">Tu nombre completo</label>
                    <input
                        id="rg-name"
                        type="text"
                        className="auth-input"
                        value={name}
                        onChange={e => setName(e.target.value)}
                        placeholder="Nombre y apellido"
                        required autoFocus autoComplete="name"
                    />
                </div>

                <div className="auth-field">
                    <label className="auth-label" htmlFor="rg-password">Contraseña</label>
                    <div className="auth-input-wrap">
                        <input
                            id="rg-password"
                            type={showPass ? 'text' : 'password'}
                            className="auth-input has-toggle"
                            value={password}
                            onChange={e => setPassword(e.target.value)}
                            placeholder="Mínimo 6 caracteres"
                            required minLength={6} autoComplete="new-password"
                        />
                        <button
                            type="button"
                            className="auth-toggle"
                            onClick={() => setShowPass(v => !v)}
                            tabIndex={-1}
                            aria-label={showPass ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                        >
                            <IconEye off={showPass} />
                        </button>
                    </div>
                </div>

                <div className="auth-field">
                    <label className="auth-label" htmlFor="rg-confirm">Confirmar contraseña</label>
                    <input
                        id="rg-confirm"
                        type={showPass ? 'text' : 'password'}
                        className="auth-input"
                        value={confirm}
                        onChange={e => setConfirm(e.target.value)}
                        placeholder="Repite la contraseña"
                        required minLength={6} autoComplete="new-password"
                    />
                </div>

                <button type="submit" className="auth-submit" disabled={loading}>
                    {loading ? <Spinner size="sm" animation="border" /> : <>Crear cuenta <IconArrow /></>}
                </button>
            </form>

            <div className="auth-footer-link">
                ¿Ya tienes cuenta? <Link to="/login">Inicia sesión</Link>
            </div>
        </AuthShell>
    );
}
